// The two in-chat band cards — pure helpers shared by the chat state (use-chat), the BFF history
// mapping (messages API), and the card UI (MessageRow/BandOfferCard, MessageRow/BandCard). Both ride
// an ASSISTANT reply exactly like `postConnectedConnectorCard`: parsed off the row's `metadata` on the
// history fetch, off the live duplexer frame, and in-band off the SSE `messageEnd` event.
//
// The offer card carries CONTENT ONLY — the pick is the same row's `choices` chips, which MessageRow
// already renders — so nothing here describes an action.
//
// Deliberately dependency-free (mirrors lib/post-connected-connector-card.ts): unit tests must not drag
// the common-lib graph, so the validation here MIRRORS common-lib's `readBandOfferCard` / `readBandCard`
// and their spec `readBandCards.spec.ts` rather than importing them. Keep the two in step.

export const BAND_CARD_APP_STATUSES = ['connected', 'skipped', 'failed', 'pending'] as const;
export type BandCardAppStatus = (typeof BAND_CARD_APP_STATUSES)[number];

/** One agent on a band card: who they are and what they own, already in the user's language. */
export type BandCardAgentData = { name: string; title: string; id?: string };

/** One app on a band card. `toolkit` is what the client matches against its connectors and routines. */
export type BandCardAppData = {
  toolkit: string;
  name: string;
  logoUrl?: string;
  status?: BandCardAppStatus;
  /** An opaque key naming the apps that do the same job, stamped server-side — the card draws a
   *  fully-connected group as one row. Treated as a string; the rule lives in common-lib. */
  group?: string;
};

/** One of the bands offered: a name, one line, the team and the apps it runs on. */
export type BandOfferCardOptionData = {
  id: string;
  name: string;
  tagline: string;
  /**
   * The outcome this band is a bet on, drawn as its own labelled section.
   *
   * The two bands differ on this before they differ on anyone hired, which is why it is offered with them
   * rather than opened afterwards. Absent on an offer made before this shipped, and the panel then draws
   * exactly as it did.
   */
  goal?: string;
  agents: BandCardAgentData[];
  apps: BandCardAppData[];
  /**
   * The connect walk for this band, armed with the offer so the pick can open it inside its own click.
   *
   * A browser opens a window only from a real user gesture, and the created band's card arrives seconds
   * after the pick — so the link has to be waiting here rather than minted by the tap. Absent when the
   * band has nothing to connect or arming failed, and the created card's own "Connect all" covers that.
   */
  connectUrl?: string;
};

/** The offer: two whole teams side by side. Two in practice; the card renders whatever it is given. */
export type BandOfferCardData = { options: BandOfferCardOptionData[] };

/**
 * The band once it exists — the team that was hired and every app it runs on with where each stands.
 *
 * NOT here: the routines. Each app row reads them live from `useRoutines()` filtered to its `toolkit`,
 * the same rule (and the same component) as the post-connected connector card — a switch drawn from the
 * turn's snapshot shows the wrong state the moment anything else moves it.
 */
/** Where one laid-out task stands. Everything is `idle` until the user gives their go. */
export type BandCardTaskStatusData = 'idle' | 'working' | 'done';

export type BandCardTaskData = {
  title: string;
  agent?: string;
  status: BandCardTaskStatusData;
};

/** The outcome the band was picked for, with the work that reaches it, waiting on the user's go. */
export type BandCardGoalData = {
  title: string;
  slug?: string;
  tasks?: BandCardTaskData[];
};

export type BandCardData = {
  name: string;
  agents: BandCardAgentData[];
  apps: BandCardAppData[];
  /** The goal, laid out and unstarted — the plan the reply is asking the user to set off. */
  goal?: BandCardGoalData;
  /** The widgets the band's connects put on the home page, by slug — the card draws the count. */
  homeWidgets?: string[];
  /**
   * The ONE connect link for every app still unconnected, drawn as a single "Connect all" on the stacked
   * apps. Absent once nothing is left to connect, and on the card a completed chain restamps.
   */
  connectUrl?: string;
};

/** A card may arrive as an object or as the JSON string a transport encoded it into. */
function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'string') {
    try {
      return asRecord(JSON.parse(value));
    } catch {
      return null;
    }
  }
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

const trimmed = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** An agent with no name cannot be drawn, so it is dropped rather than failing the whole team. */
function readAgents(raw: unknown): BandCardAgentData[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    const agent = asRecord(entry);
    const name = trimmed(agent?.name);
    const id = trimmed(agent?.id);
    return name ? [{ name, title: trimmed(agent?.title), ...(id ? { id } : {}) }] : [];
  });
}

/** `logoUrl` and `status` are dropped item by item: a bad logo must not cost the app its row. */
function readApps(raw: unknown): BandCardAppData[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    const app = asRecord(entry);
    const toolkit = trimmed(app?.toolkit);
    const name = trimmed(app?.name);
    if (!toolkit || !name) return [];
    const logoUrl = app?.logoUrl;
    const status = app?.status;
    return [
      {
        toolkit,
        name,
        ...(typeof logoUrl === 'string' && /^https?:\/\/\S+$/i.test(logoUrl) ? { logoUrl } : {}),
        ...(typeof status === 'string' && (BAND_CARD_APP_STATUSES as readonly string[]).includes(status)
          ? { status: status as BandCardAppStatus }
          : {}),
        ...(trimmed(app?.group) ? { group: trimmed(app?.group) } : {}),
      },
    ];
  });
}

/**
 * Read the band offer off a message's metadata, or `null` when the row carries none.
 *
 * Fails closed on an offer with no usable option: an option needs a name and at least one agent, since
 * a team with nobody on it is not a band. An option missing its `id` takes its 1-based position, so the
 * chip that picks it still has something to name back.
 */
export function readBandOfferCard(metadata: unknown): BandOfferCardData | null {
  const card = asRecord(asRecord(metadata)?.bandOfferCard);
  if (!card || !Array.isArray(card.options)) return null;
  const options: BandOfferCardOptionData[] = card.options.flatMap((entry, index) => {
    const option = asRecord(entry);
    const name = trimmed(option?.name);
    const agents = readAgents(option?.agents);
    if (!name || agents.length === 0) return [];
    // http(s) only, the same guard `connectUrl` takes on the band card below: a tap on this option opens
    // it, so a forged `javascript:` or `data:` would run as a link the owner was told to tap.
    const connectUrl = /^https?:\/\/\S+$/i.test(trimmed(option?.connectUrl)) ? trimmed(option?.connectUrl) : '';
    return [
      {
        id: trimmed(option?.id) || String(index + 1),
        name,
        tagline: trimmed(option?.tagline),
        ...(trimmed(option?.goal) ? { goal: trimmed(option?.goal) } : {}),
        agents,
        apps: readApps(option?.apps),
        ...(connectUrl ? { connectUrl } : {}),
      },
    ];
  });
  return options.length > 0 ? { options } : null;
}

/** Read the band card off a message's metadata, or `null`. Same floor as an offer option: a name and a team. */
export function readBandCard(metadata: unknown): BandCardData | null {
  const card = asRecord(asRecord(metadata)?.bandCard);
  const name = trimmed(card?.name);
  const agents = readAgents(card?.agents);
  if (!card || !name || agents.length === 0) return null;
  const homeWidgets = Array.isArray(card.homeWidgets) ? card.homeWidgets.map(trimmed).filter(Boolean) : [];
  // http(s) only, the same guard the two native parsers take and the one `logoUrl` already takes here: a
  // forged `javascript:` or `data:` in this field would otherwise reach the browser as a link the owner
  // is being told to click.
  const connectUrl = /^https?:\/\/\S+$/i.test(trimmed(card.connectUrl)) ? trimmed(card.connectUrl) : '';
  const goal = readGoal(card.goal);
  return {
    name,
    agents,
    apps: readApps(card.apps),
    ...(goal ? { goal } : {}),
    ...(homeWidgets.length > 0 ? { homeWidgets } : {}),
    ...(connectUrl ? { connectUrl } : {}),
  };
}

const TASK_STATUSES: readonly BandCardTaskStatusData[] = ['idle', 'working', 'done'];

/** A task with no title cannot be drawn; an unknown status reads as idle rather than losing the row. */
function readGoal(raw: unknown): BandCardGoalData | undefined {
  const goal = asRecord(raw);
  const title = trimmed(goal?.title);
  if (!title) return undefined;
  const tasks = (Array.isArray(goal?.tasks) ? goal.tasks : []).flatMap((entry) => {
    const task = asRecord(entry);
    const taskTitle = trimmed(task?.title);
    if (!taskTitle) return [];
    const status = TASK_STATUSES.includes(trimmed(task?.status) as BandCardTaskStatusData)
      ? (trimmed(task?.status) as BandCardTaskStatusData)
      : 'idle';
    const agent = trimmed(task?.agent);
    return [{ title: taskTitle, status, ...(agent ? { agent } : {}) }];
  });
  const slug = trimmed(goal?.slug);
  return { title, ...(slug ? { slug } : {}), ...(tasks.length > 0 ? { tasks } : {}) };
}

/**
 * What a band event reports about what was on screen.
 *
 * Analytics asked for "the list of agents, connectors and routines offered", and the first cut sent
 * COUNTS (`agents: "3"`, `apps: "4"`). A count answers how many and nothing else: it cannot say which
 * band converted, which connector is the one people refuse, or whether the agent mix matters. These
 * build the lists instead, in one place, so the impression and the clicks about the same card can never
 * describe it differently.
 *
 * `connectors` rather than `apps`: it is the word the analyst asked in, and the toolkit slug is what
 * joins to every other connector event we send.
 */
const joinNames = (values: (string | undefined)[]): string | undefined => {
  const unique = [...new Set(values.map((value) => value?.trim()).filter((value): value is string => Boolean(value)))];
  return unique.length > 0 ? unique.join(',') : undefined;
};

/** The offer impression: which bands, and everyone and everything they put on the table. */
export function bandOfferSeenFields(card: BandOfferCardData): Record<string, string | undefined> {
  return {
    options: String(card.options.length),
    bands: joinNames(card.options.map((option) => option.id)),
    agents: joinNames(card.options.flatMap((option) => option.agents.map((agent) => agent.name))),
    connectors: joinNames(card.options.flatMap((option) => option.apps.map((app) => app.toolkit))),
    // No routines here on purpose: an OFFER carries none. They exist once the band is created and its
    // connectors land, which is what the created card's own fields report. An empty string would read
    // as "none offered" rather than "not a thing this card has".
  };
}

/** The band a reader picked: what to send as their turn, and what the click should report about it. */
export type BandPick = { id: string; name: string; agents?: string; connectors?: string };

/** One band on the offer, for the click that picks it — "what they click on", in full. */
export function bandOptionFields(option: BandOfferCardOptionData): BandPick {
  return {
    id: option.id,
    name: option.name,
    agents: joinNames(option.agents.map((agent) => agent.name)),
    connectors: joinNames(option.apps.map((app) => app.toolkit)),
  };
}

/** The created band's impression: the team, what it runs on, and the routines standing behind it. */
export function bandCardSeenFields(card: BandCardData, routineNames: string[]): Record<string, string | undefined> {
  return {
    band: card.name,
    agents: joinNames(card.agents.map((agent) => agent.name)),
    connectors: joinNames(card.apps.map((app) => app.toolkit)),
    routines: joinNames(routineNames),
    homeWidgets: joinNames(card.homeWidgets ?? []),
  };
}

/**
 * The connect-chain link, told which client is opening it.
 *
 * The chain's finish page ends differently per client — a browser tab closes itself, the app is reopened
 * — and the client is the ONLY thing that knows which. It cannot be derived server-side: the first cut
 * read the conversation's `CommunicationChannel`, which says how Symphony REACHES the user, so an
 * app-installed user reading on the desktop was handed the app ending and the browser answered
 * `Failed to launch 'orion://chat/…' because the scheme does not have a registered handler`.
 *
 * Mirrored on both phones, which append `from=app`.
 */
export function bandConnectUrlFrom(connectUrl: string, from: 'web' | 'app'): string {
  return `${connectUrl}${connectUrl.includes('?') ? '&' : '?'}from=${from}`;
}

/** A band app whose connect did not happen: no routines to expand to, and no count to draw. */
export function isBandAppConnectable(app: BandCardAppData): boolean {
  return app.status !== 'skipped' && app.status !== 'failed';
}

/**
 * Whether a settled row opens, and — through its one caller — whether it carries a routine count.
 *
 * A not-connected app has nothing behind it to show, and an app with no routines would open onto an
 * empty panel. `chainPending` is the third: while the chain is still walking apps the rows say only
 * where each app stands, because a count beside an app mid-connect is a second number competing with
 * the one the owner is actually waiting on, about work that has not started yet.
 *
 * One value gates the chevron AND the count. Zeroing the routine list instead closes the row but leaves
 * the label rendering "0 routines on", which is a worse thing to say than nothing — that is exactly what
 * the first cut of this did on Android.
 *
 * Mirrored 1:1 with iOS's `BandChatCard.isExpandable` and Android's `bandRowExpandable`.
 */
export function bandRowExpandable(connectable: boolean, routineCount: number, chainPending = false): boolean {
  return !chainPending && connectable && routineCount > 0;
}

/**
 * An app's status as the card should DRAW it: its own, upgraded to `connected` when the LIVE connectors
 * list says so.
 *
 * This is what makes the card FINISH. The marker is stamped when the chain is handed over, so every app
 * in it reads `pending` forever — the owner connects four apps in the browser, comes back, and the card
 * still says "Not connected" beside a button asking them to do it again. Reading the live list instead
 * means the row settles the moment the connect lands, exactly as a connector card's does, and the poll is
 * only how the card notices while it is still on screen.
 *
 * EVERY status read on the band card goes through this — a second, direct `app.status` read is the bug
 * returning on whichever half of the card forgot.
 *
 * Toolkit casing is normalized on both sides, the same rule `isToolkitConnected` takes in
 * `lib/connector-card.ts`: the status payload and the card's marker are minted by different services and
 * a case mismatch would silently leave the card unfinished.
 *
 * Pure, and mirrored 1:1 with iOS `BandChatCard.effectiveStatus` and Android's twin.
 */
export function bandAppEffectiveStatus(app: BandCardAppData, connectedToolkits: ReadonlySet<string>): BandCardAppStatus | undefined {
  for (const toolkit of connectedToolkits) {
    if (toolkit.toLowerCase() === app.toolkit.toLowerCase()) return 'connected';
  }
  return app.status;
}

/**
 * The toolkits the LIVE connectors list reports as connected — the input every status read on the band
 * card takes. Structurally typed rather than importing `ConnectorStatus`, so this module stays
 * dependency-free and unit-testable.
 */
export function connectedToolkitsOf(connectors: readonly { toolkit?: string; connected?: boolean }[] | undefined): Set<string> {
  return new Set((connectors ?? []).filter((c) => c.connected === true).map((c) => c.toolkit ?? ''));
}

/**
 * A missing status is PENDING: the card is stamped before the chain runs, and an app it has not reached
 * yet is exactly the case the connect cluster exists for.
 */
export function isBandAppPending(status: BandCardAppStatus | undefined): boolean {
  return status === undefined || status === 'pending';
}

/** The band card's two app groups — the chain's cluster of chips, and the rows below it. */
export type BandAppSplitData = { cluster: BandCardAppData[]; settled: BandCardAppData[] };

/**
 * The card's two app groups, split.
 *
 * **The CARD decides, not the moment.** A card that carries a `connectUrl` is the chain's own card and
 * keeps the cluster for its whole life; a card without one is the completion card and draws settled rows.
 * The split used to key on whether anything was still pending, which meant the chain card RE-RENDERED as
 * the completion card the moment the last app landed — so a transcript showed the same band twice, in two
 * identical cards, one of them under a reply still saying "tap Connect all on the card below".
 *
 * `cluster` is the chain's own checklist. A connected app STAYS in it rather than moving down to a row of
 * its own, which is what lets the cluster fill in with checks as the owner works through the browser —
 * and what leaves a finished chain reading as a finished checklist rather than as a second card.
 *
 * `settled` is everything the chain is not accounting for: a skipped or failed app while it runs, and
 * every app on the completion card. The two are disjoint by construction — listing an app as a chip AND
 * as a row underneath says the same thing twice in two treatments, which reads as two different apps.
 *
 * Pure, so the split is assertable without rendering and cannot drift from the phones'. Mirrored 1:1 with
 * iOS `BandChatCard.appSplit` and Android's twin.
 */
export function bandAppSplit(
  apps: BandCardAppData[],
  connectUrl: string | undefined,
  connectedToolkits: ReadonlySet<string>,
): BandAppSplitData {
  const status = (app: BandCardAppData) => bandAppEffectiveStatus(app, connectedToolkits);
  // No chain on this card, so nothing to cluster around and every app keeps its own row.
  if (!connectUrl) return { cluster: [], settled: apps };
  const cluster = apps.filter((app) => isBandAppPending(status(app)) || status(app) === 'connected');
  const inCluster = new Set(cluster.map((app) => app.toolkit));
  return { cluster, settled: apps.filter((app) => !inCluster.has(app.toolkit)) };
}

/** The connect poll's tick, and how many of them it gets. Mirrors iOS's `3_000_000_000` × 40 loop. */
export const BAND_CONNECT_POLL_INTERVAL_MS = 3_000;
export const BAND_CONNECT_POLL_TICKS = 40;

/**
 * Whether the connect poll is still inside its bounded window. An owner who abandons the browser tab
 * must not leave a request loop behind them; the focus refetch is what catches a connect they finish
 * later.
 */
export function shouldPollBandConnect(startedAt: number, now: number): boolean {
  return now - startedAt <= BAND_CONNECT_POLL_INTERVAL_MS * BAND_CONNECT_POLL_TICKS;
}

/** One row of the band card's app list: a single app, or the apps of one group drawn together. */
export type BandAppRowData = { key: string; apps: BandCardAppData[] };

/**
 * Fold the apps that do the same job into one row — Instagram and Facebook are both where the business
 * posts, and two rows saying so is the card repeating itself.
 *
 * It folds only when EVERY app of that group on this card is CONNECTED, which is the whole condition. A
 * group holding one connected app and one that failed has two different things to say, and a merged row
 * could say neither: the reader would lose which of the two needs them. So a group is one row exactly
 * when there is one fact to report about all of it.
 *
 * Order is the first member's, so folding never moves an app up the list past something that was above
 * it. Pure and dependency-free, and mirrored on both phones (`bandAppRows` / `BandChatCard.rows`).
 */
export function bandAppRows(apps: BandCardAppData[], connectedToolkits: ReadonlySet<string> = new Set()): BandAppRowData[] {
  // The EFFECTIVE status, like every other read on this card. Folding on the marker's own status meant a
  // group whose members the live connectors list had just settled stayed as two rows until the marker
  // restamped — the one place the card still answered from the payload.
  const foldable = new Set(
    [...new Set(apps.map((app) => app.group).filter(Boolean))].filter((group) => {
      const members = apps.filter((app) => app.group === group);
      return members.length > 1 && members.every((app) => bandAppEffectiveStatus(app, connectedToolkits) === 'connected');
    }) as string[],
  );
  const rows: BandAppRowData[] = [];
  const drawn = new Set<string>();
  for (const app of apps) {
    const group = app.group && foldable.has(app.group) ? app.group : undefined;
    if (!group) {
      rows.push({ key: app.toolkit, apps: [app] });
      continue;
    }
    if (drawn.has(group)) {
      continue;
    }
    drawn.add(group);
    rows.push({ key: `group:${group}`, apps: apps.filter((member) => member.group === group) });
  }
  return rows;
}
